import React, { useState } from "react";
import { useMatch } from "react-router-dom";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Text from "components/atoms/text/Text";

import Constants from "utils/constants";
import { restartReferenceFileIndexing } from "utils/request/bibliography";
import { apiSpaceUrl } from "utils/urls";
import { isReadonlyUser } from "utils/user";

import axios from "axios";
import { saveAs } from "file-saver";

const Container = styled.div`
    width: 100%;
`;

const CardContainer = styled.div`
    margin: 8px;
    width: calc(100% - 16px);
    height: auto;
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    box-shadow: 1px 1px 8px rgba(0, 0, 0, 0.1);
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: 8px;
    background-color: var(--color-BaseBlue);
`;

const CardBody = styled.div`
    display: flex;
    flex-direction: column;

    padding: 8px;
    gap: 8px;
`;

const CardFooter = styled.div`
    padding: 8px;

    display: flex;
    gap: 8px;

    border-top: 1px solid var(--color-Outline);
`;

const CardRow = styled.div`
    display: flex;
    align-items: center;
`;

const CardRowDivideLine = styled.div`
    display: flex;
    margin: 0;
    width: 100%;
    height: 1px;
    border-top: 1px dashed var(--color-Line);
`;

const CardTitleText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Black)",
})``;

const CardText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})``;

const IndexingInfoGaugeRow = styled.div`
    position: relative;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background-color: var(--color-Grey3);
    overflow: hidden;
`;

const IndexingInfoGauge = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 8px;
    background-color: ${(props) => (props.warn ? "var(--color-Warn)" : "var(--color-Key)")};
    width: ${(props) => props.gauge || 0}%;
    transition: width 0.3s ease;
`;

function PDFChatFileStatusView(props) {
    const match = useMatch("/:space_uuid/*");
    const space_uuid = match?.params?.space_uuid ?? "";
    // region: PDF 파일 관리 카드
    const [isFileInfoCardOpen, setIsFileInfoCardOpen] = useState(false);

    const handleDownloadFile = (e) => {
        e.stopPropagation();
        if (props.reference?.file) {
            saveAs(props.reference.file, `${decodeURI(props.reference.file.split("/").pop())}`);
        }
    };

    const handleDeleteFile = (e) => {
        e.stopPropagation();
        Confirm("warn", "PDF 삭제", "PDF 파일을 삭제하시겠습니까? 대화 기록도 함께 삭제됩니다.", "삭제", (e) => {
            axios
                .delete(apiSpaceUrl("bibliography", space_uuid) + "/file/reference", {
                    data: { reference_id: props.reference.id },
                })
                .catch((error) => {
                    console.log(error);
                    Alert("warn", "에러", "PDF 파일 삭제 중 오류가 발생하였습니다.\n잠시후 다시 시도해주세요.");
                });
        });
    };

    // region: PDF 자동 분석 카드
    const [isAnalysisCardOpen, setIsAnalysisCardOpen] = useState(false);

    const indexed_pages = props.pdfIndexingInfo.indexed_pages;
    const total_pages = props.pdfIndexingInfo.total_pages;
    const analysis_status = props.pdfIndexingInfo.analysis_status;

    const handleIndexingRestart = async () => {
        try {
            await restartReferenceFileIndexing(space_uuid, props.reference.file_id);
            Alert("info", "알림", "인덱싱을 다시 시작합니다.");
        } catch (e) {
            console.log(e);
            Alert("warn", "에러", "요청이 실패했습니다.");
        }
    };

    // region: 마크업
    return (
        <Container>
            {/* PDF 파일 관리 카드 */}
            <CardContainer>
                <CardHeader>
                    <CardTitleText>PDF 파일 관리</CardTitleText>
                    <Tooltip message={isFileInfoCardOpen ? "닫기" : "열기"}>
                        <Button
                            onlyIcon
                            bgColor={"var(--color-BaseBlue)"}
                            hoverBgColor={"var(--color-ButtonHover4)"}
                            fontColor={"var(--color-SubBlack)"}
                            onClick={(e) => {
                                setIsFileInfoCardOpen(!isFileInfoCardOpen);
                            }}
                        >
                            <Icon name={isFileInfoCardOpen ? "arrowUp" : "arrowDown"} color={"var(--color-SubBlack)"} />
                        </Button>
                    </Tooltip>
                </CardHeader>
                {isFileInfoCardOpen && (
                    <>
                        <CardBody>
                            <CardRow style={{ justifyContent: "space-between" }}>
                                <div style={{ display: "flex" }}>
                                    <Icon name="ai" size={"14"} color={"var(--color-Grey1)"} />
                                    <CardText style={{ marginLeft: "4px" }}>파일 정보</CardText>
                                </div>
                                <div style={{ display: "flex", gap: "4px" }}>
                                    <Tooltip message="PDF 다운로드">
                                        <Button
                                            onlyIcon
                                            buttonSize={"16px"}
                                            bgColor={"var(--color-White)"}
                                            hoverBgColor={"var(--color-ButtonHover4)"}
                                            borderRadius={"2px"}
                                            onClick={handleDownloadFile}
                                            disabled={isReadonlyUser(props?.user)}
                                        >
                                            <Icon name={"download"} size={"12"} color={"var(--color-SubBlack)"} />
                                        </Button>
                                    </Tooltip>
                                    {analysis_status !== Constants.FILE_ANALYSIS_DOING && (
                                        <Tooltip message="PDF 삭제">
                                            <Button
                                                onlyIcon
                                                buttonSize={"16px"}
                                                bgColor={"var(--color-White)"}
                                                hoverBgColor={"var(--color-ButtonHover4)"}
                                                borderRadius={"2px"}
                                                onClick={handleDeleteFile}
                                                disabled={isReadonlyUser(props?.user)}
                                            >
                                                <Icon name={"delete"} size={"12"} color={"var(--color-SubBlack)"} />
                                            </Button>
                                        </Tooltip>
                                    )}
                                </div>
                            </CardRow>
                            <CardRow>
                                <CardText ellipsis>{decodeURI(props.reference.file.split("/").pop())}</CardText>
                            </CardRow>
                        </CardBody>
                    </>
                )}
            </CardContainer>

            {/* PDF 자동 분석 카드 */}
            <CardContainer>
                <CardHeader>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <CardTitleText>PDF 자동 분석</CardTitleText>
                        {analysis_status === Constants.FILE_ANALYSIS_TODO ||
                            (analysis_status === Constants.FILE_ANALYSIS_DOING && (
                                <Tooltip message={"분석중입니다"}>
                                    <AiCursor />
                                </Tooltip>
                            ))}
                    </div>
                    <Tooltip message={isAnalysisCardOpen ? "닫기" : "열기"}>
                        <Button
                            onlyIcon
                            bgColor={"var(--color-BaseBlue)"}
                            hoverBgColor={"var(--color-ButtonHover4)"}
                            fontColor={"var(--color-SubBlack)"}
                            onClick={(e) => {
                                setIsAnalysisCardOpen(!isAnalysisCardOpen);
                            }}
                        >
                            <Icon name={isAnalysisCardOpen ? "arrowUp" : "arrowDown"} color={"var(--color-SubBlack)"} />
                        </Button>
                    </Tooltip>
                </CardHeader>
                {isAnalysisCardOpen && (
                    <>
                        <CardBody>
                            {/* 인덱싱 */}
                            <CardRow>
                                <Icon name="ai" size={"14"} color={"var(--color-Grey1)"} />
                                <CardText style={{ marginLeft: "4px" }}>인덱싱 현황</CardText>
                            </CardRow>
                            <CardRow>
                                <CardText>
                                    AI 가 PDF 파일의 내용을 바탕으로 답변하기 위해, 파일의 구조를 분석하고 검색 가능한
                                    형태로 변환합니다.
                                </CardText>
                            </CardRow>
                            <CardRow style={{ display: "flex", justifyContent: "space-between" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <CardTitleText style={{ margin: "8px 0px 8px 0px", color: "var(--color-DarkKey)" }}>
                                        {"STATUS: " + analysis_status}
                                    </CardTitleText>
                                    {analysis_status === Constants.FILE_ANALYSIS_TODO ||
                                        (analysis_status === Constants.FILE_ANALYSIS_DOING && (
                                            <Tooltip message={"분석중입니다"}>
                                                <Icon name={"load"} size={"12"}></Icon>
                                            </Tooltip>
                                        ))}
                                </div>
                                <CardText>{indexed_pages + "/" + total_pages} 페이지</CardText>
                            </CardRow>
                            <CardRow>
                                <IndexingInfoGaugeRow>
                                    <IndexingInfoGauge gauge={(indexed_pages / total_pages) * 100} />
                                </IndexingInfoGaugeRow>
                            </CardRow>
                            {analysis_status === Constants.FILE_ANALYSIS_FAILED && (
                                <CardRow>
                                    <Button
                                        onlyText
                                        width={"100%"}
                                        height={"24px"}
                                        bgColor={"var(--color-Button1)"}
                                        hoverBgColor={"var(--color-ButtonHover1)"}
                                        fontSize={"small"}
                                        fontColor={"var(--color-White)"}
                                        buttonText={"인덱싱 다시 시작"}
                                        onClick={handleIndexingRestart}
                                    />
                                </CardRow>
                            )}

                            {/* 요약 */}
                            <CardRowDivideLine />
                            <CardRow>
                                <Icon name="ai" size={"14"} color={"var(--color-Grey1)"} />
                                <CardText style={{ marginLeft: "4px" }}>논문 요약</CardText>
                            </CardRow>
                            <CardRow>
                                <CardText>
                                    연구 주제, 연구 방법, 연구 결과를 간결하게 요약하여 채팅 메시지를 생성합니다.
                                </CardText>
                            </CardRow>
                            <CardRow>
                                <Button
                                    onlyText
                                    width={"100%"}
                                    height={"24px"}
                                    bgColor={"var(--color-Button1)"}
                                    hoverBgColor={"var(--color-ButtonHover1)"}
                                    fontSize={"small"}
                                    fontColor={"var(--color-White)"}
                                    buttonText={"분석 시작"}
                                    onClick={(e) => {
                                        let req_message = {
                                            autoResponse: true,
                                            content:
                                                "이 논문의 전체 페이지를 확인한 후, 이 논문의 연구 주제, 연구 방법, 연구 결과에 대해 쉽고 간결하게 요약해줘.",
                                            reviewBoxId: null,
                                            reviewBoxColor: null,
                                        };
                                        props.chatStart(req_message);
                                    }}
                                    disabled={
                                        analysis_status !== Constants.FILE_ANALYSIS_DONE || isReadonlyUser(props.user)
                                    }
                                />
                            </CardRow>

                            {/* 비평 */}
                            <CardRowDivideLine />
                            <CardRow>
                                <Icon name="ai" size={"14"} color={"var(--color-Grey1)"} />
                                <CardText style={{ marginLeft: "4px" }}>논문 비평</CardText>
                            </CardRow>
                            <CardRow>
                                <CardText>
                                    논문의 훌륭한 점과 아쉬운 점, 보완 방향을 제시하는 채팅 메시지를 생성합니다.
                                </CardText>
                            </CardRow>
                            <CardRow>
                                <Button
                                    onlyText
                                    width={"100%"}
                                    height={"24px"}
                                    bgColor={"var(--color-Button1)"}
                                    hoverBgColor={"var(--color-ButtonHover1)"}
                                    fontSize={"small"}
                                    fontColor={"var(--color-White)"}
                                    buttonText={"분석 시작"}
                                    onClick={(e) => {
                                        let req_message = {
                                            autoResponse: true,
                                            content:
                                                "이 논문의 전체 페이지를 확인한 후, 전문가의 관점으로 이 논문의 훌륭한 점, 아쉬운 점, 보완 방향을 제시하고 종합적인 평가를 내려줘.",
                                            reviewBoxId: null,
                                            reviewBoxColor: null,
                                        };
                                        props.chatStart(req_message);
                                    }}
                                    disabled={
                                        analysis_status !== Constants.FILE_ANALYSIS_DONE || isReadonlyUser(props.user)
                                    }
                                />
                            </CardRow>
                        </CardBody>
                    </>
                )}
            </CardContainer>
        </Container>
    );
}

export default PDFChatFileStatusView;
